import type { LocationRecordMap } from '@libs/data-access/entities'
import { Alert, Badge, MultiSelect, Select } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import { useMemoSelector } from '@spa/redux/hooks'
import { membershipsSelectors } from '@spa/scenes/Plannings/redux/selectors'
import { getMostRecentDate, getOldestDate } from '@spa/utils/date'
import { isEqual } from 'lodash-es'
import React, { useEffect } from 'react'
import type { FC } from 'react'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'
import { isContractInFullProgressBetweenLegacy } from 'snap-redux/modules/contracts'
import { getPlannableMembershipsByIds } from 'snap-redux/modules/memberships/selectors'

import { usePlannableContractsLegacy } from '../../hooks'
import { UNASSIGNED_SHIFT } from '../../utils/createShiftArrayPayload'
import { ContractIsntFullyAvailable } from '../Alerts'

type SelectItemType = {
  label?: string
  value?: number
  badgeColor?: string
  badgeVariant?: string
  highlight?: string
}

type Props = {
  control: Control
  errors: FieldErrors
  disabled: boolean
  isUpdatingWeeklyschedule: boolean
  locations: LocationRecordMap
  shiftDateISO: string
  weeklyScheduleTeamId: number
  haveMultipleUserWithError: number
  isCreation: boolean
  valueState: SelectItemType[]
  setValue: Function
  selectedDays: string[]
}
/**
 * @deprecated NON
 */
export const UserContractFieldLegacy: FC<Props> = ({
  control,
  errors,
  disabled,
  isUpdatingWeeklyschedule,
  locations,
  shiftDateISO,
  weeklyScheduleTeamId,
  haveMultipleUserWithError,
  isCreation,
  valueState,
  setValue,
  selectedDays
}) => {
  const { t } = usePlanningFormTranslation()
  const contracts = usePlannableContractsLegacy(
    shiftDateISO,
    weeklyScheduleTeamId
  )

  const membershipsIds = contracts
    .map(contract => contract?.membership_id as number)
    .toArray()

  const plannedMemberships = useMemoSelector(state =>
    getPlannableMembershipsByIds(state, membershipsIds)
  ).reduce((acc, membership) => {
    acc[membership.id] = membership
    return acc
  }, {})

  // Depending on the selected views, the memberships are not set in the same redux store
  // Month View v2 uses plannedMemberships
  const memberships =
    membershipsSelectors.useGetCachedMap() ?? plannedMemberships

  /**
   * The group by here is trying to find the location id as a key of locations
   * but locations is an array, not an object so it groups all contracts in the key undefined (locations.get(ID) ==> undefined since it's an array)
   * this is really a strange behavior that I don't really understand and that is not moved to the new component UserContractField
   * */
  const usersContractOptions = contracts
    .groupBy(uc => (uc ? locations.get(uc.get('location_id')) : null))
    .entrySeq()
    // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
    .map(([, contracts]) =>
      contracts.toArray().map(contract => {
        const defaultValueState = {
          label: t('common.app:models.fullname', {
            value: {
              firstname: memberships[contract.membership_id]?.firstname,
              lastname: memberships[contract.membership_id]?.lastname
            }
          }),
          value: contract.id
        }

        const isBetweenDate = isContractInFullProgressBetweenLegacy(
          contract,
          getOldestDate(selectedDays),
          getMostRecentDate(selectedDays)
        )

        if (!isBetweenDate && selectedDays.length) {
          Object.assign(defaultValueState, {
            badgeColor: 'semantic.red',
            badgeVariant: 'outlined',
            highlight: 'danger'
          })
        }

        return defaultValueState
      })
    )
    .toJS()[0]

  if (!isCreation) {
    const emptyValue = {
      label: t('component.fieldUserContract.unassignedLabel'),
      value: UNASSIGNED_SHIFT
    }
    usersContractOptions.unshift(emptyValue)
  }

  // We want the object (label, value, highlight), not just the number value
  const handleChange = (newValues: string[] | string) => {
    const formattedData = Array.isArray(newValues) ? newValues : [newValues]
    const updatedValues = formattedData.map(item => {
      return usersContractOptions.find(option => option.value === item)
    })
    setValue('user_contract', updatedValues)
  }

  // Update highlight state of the values when the options are updated
  useEffect(() => {
    const updatedValueState = valueState.map(value => {
      if (usersContractOptions.length === 0) {
        return value
      }
      return usersContractOptions.find(option => option.value === value?.value)
    })

    // I'm checking if the array are the same or not to avoid useless rerender.
    if (!isEqual(updatedValueState, valueState)) {
      setValue('user_contract', updatedValueState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersContractOptions])

  const SelectType = isCreation ? MultiSelect : Select

  return (
    <>
      <Controller
        name='user_contract'
        control={control}
        render={({ field: { value } }) => (
          <SelectType
            clearable
            searchable
            fillWidth
            dropdownHeight='m'
            dropdownWidth='target'
            options={usersContractOptions}
            className='mb-2'
            onChange={handleChange}
            searchAdapter='fuzzyIncludingGroup'
            placeholder={t('component.fieldUserContract.placeholder')}
            value={
              // Value is an array, in modification we only handle the first value of the array
              isCreation
                ? value.filter(x => !!x).map(x => (x ? x.value : x))
                : value[0]?.value ?? -1
            }
            errorMessage={formatErrors({
              inputName: 'user_contract',
              errors
            })}
            disabled={disabled}
            label={
              <>
                {t('component.fieldUserContract.title', {
                  count: valueState.length
                })}
                {isCreation && (
                  <Badge
                    size='s'
                    label={valueState.length.toString()}
                    className='ml-2'
                  />
                )}
              </>
            }
          />
        )}
      />
      {isUpdatingWeeklyschedule && (
        <Alert intent='semantic.blue'>
          {t('component.fieldUserContract.trick')}
        </Alert>
      )}
      {haveMultipleUserWithError > 0 && (
        <ContractIsntFullyAvailable isMulti={haveMultipleUserWithError > 1} />
      )}
    </>
  )
}
